import { POSTHOG_SCHEMA_VERSION } from '@/lib/posthogIdentity';
import { isPostHogCapturing } from '@/lib/posthog';
import { trackEvent } from '@/lib/trackEvent';

const STORAGE_PREFIX = 'ph_report_read_session:';
const SESSION_TTL_MS = 60 * 60 * 1000;
const ACTIVITY_INTERVAL_MS = 60_000;

type StoredReadSession = {
  session_id: string;
  last_activity_at: number;
  emitted: boolean;
};

const storageKey = (reportId: string) => `${STORAGE_PREFIX}${reportId}`;

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const readStoredSession = (reportId: string): StoredReadSession | null => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(reportId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredReadSession>;
    if (typeof parsed.session_id !== 'string' || !parsed.session_id) return null;
    if (typeof parsed.last_activity_at !== 'number' || !Number.isFinite(parsed.last_activity_at)) {
      return null;
    }
    return {
      session_id: parsed.session_id,
      last_activity_at: parsed.last_activity_at,
      emitted: parsed.emitted === true,
    };
  } catch {
    return null;
  }
};

const writeStoredSession = (reportId: string, value: StoredReadSession): void => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(storageKey(reportId), JSON.stringify(value));
};

const isSessionExpired = (session: StoredReadSession, now: number): boolean =>
  now - session.last_activity_at > SESSION_TTL_MS;

export const ensureReportReadSession = (
  reportId: string,
  extra?: Record<string, unknown>
): string | null => {
  if (!reportId) return null;

  const now = Date.now();
  const stored = readStoredSession(reportId);
  const isNew = !stored || isSessionExpired(stored, now);

  const session: StoredReadSession = isNew
    ? { session_id: createSessionId(), last_activity_at: now, emitted: false }
    : { ...stored, last_activity_at: now };

  if (isPostHogCapturing() && !session.emitted) {
    const tracked = trackEvent('report_session_started', {
      report_id: reportId,
      session_id: session.session_id,
      schema_version: POSTHOG_SCHEMA_VERSION,
      ...extra,
    });
    if (tracked) {
      session.emitted = true;
    }
  }

  writeStoredSession(reportId, session);
  return session.session_id;
};

export const touchReportReadSession = (reportId: string): void => {
  if (!reportId) return;
  const stored = readStoredSession(reportId);
  if (!stored) {
    ensureReportReadSession(reportId);
    return;
  }
  writeStoredSession(reportId, {
    ...stored,
    last_activity_at: Date.now(),
  });
};

export const startReportReadSessionTracking = (
  reportId: string,
  extra?: Record<string, unknown>
): (() => void) => {
  if (!reportId || typeof window === 'undefined') {
    return () => undefined;
  }

  ensureReportReadSession(reportId, extra);

  const onActivity = () => {
    touchReportReadSession(reportId);
  };

  const onVisibility = () => {
    if (document.hidden) return;
    onActivity();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onActivity);
  const intervalId = window.setInterval(onActivity, ACTIVITY_INTERVAL_MS);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onActivity);
    window.clearInterval(intervalId);
  };
};
